import React, { useState, useEffect, useRef, useMemo } from 'react'
import styled, { keyframes } from 'styled-components'
import { motion, AnimatePresence } from 'framer-motion'
import { FaPlay, FaPause, FaImage, FaFile, FaChevronLeft, FaChevronRight, FaCheckCircle, FaBriefcase, FaMoneyBillWave, FaBolt, FaThumbtack, FaBell } from 'react-icons/fa'
import { MdNewReleases } from 'react-icons/md'
import { GiGong, GiTribalGear, GiTribalMask } from 'react-icons/gi'
import Confetti from 'react-confetti-boom'
import fallbackImg from '../windows-xp-2-912x569.jpg'
import accepted1 from '../assets/sounds/accepted1.mp3'
import accepted2 from '../assets/sounds/accepted2.mp3'
import accepted3 from '../assets/sounds/accepted3.mp3'
import newOpportunitySound from '../assets/sounds/new_opportunity.mp3'
import projectBilledSound from '../assets/sounds/project_billed.mp3'
import egeEnergySound from '../assets/sounds/ege_energy.mp3'

const explode = keyframes`
  0% {
    opacity: 0;
    transform: translate(0, 0) rotate(0) scale(1);
  }
  10% {
    opacity: 1;
  }
  80% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform:
      translate(var(--tx), var(--ty))
      rotate(var(--r))
      scale(var(--s));
  }
`

const Container = styled(motion.div)`
  position: fixed; inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const Bill = styled.div`
  position: absolute;
  font-size: 48px;
  will-change: transform, opacity;
  animation: ${explode} 6.5s forwards;
  animation-delay: var(--delay);
`

const CarouselContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 15px;
  background: rgba(43, 43, 43, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
`

const MediaContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  /* Container 16:9 */
  &::before {
    content: '';
    display: block;
    width: 100%;
    padding-top: 56.25%; /* 16:9 aspect ratio (9/16 = 0.5625) */
  }
`

const MediaWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const SlideContainer = styled(motion.div)`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 15px;
  overflow: hidden;
`

const SlideImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 15px;
`

const SlideContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(43, 43, 43, 0.3));
  padding: 30px;
  color: white;
`

const Controls = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  display: flex;
  gap: 10px;
  z-index: 10;
`

const ControlButton = styled.button`
  background: rgba(43, 43, 43, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  
  &:hover {
    background: rgba(43, 43, 43, 0.5);
    border-color: #00d4ff;
    transform: scale(1.1);
  }
`

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  width: 100%;
`

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: #00d4ff;
  border-radius: 2px;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #888;
  font-size: 18px;
####
    margin-bottom: 20px;
    opacity: 0.5;
  }
`

const NotificationOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => {
    switch (props.eventType) {
      case 'opportunity_accepted': return 'linear-gradient(135deg, rgba(255, 193, 7, 1), rgba(255, 193, 7, 1))'
      case 'new_opportunity': return 'linear-gradient(135deg, rgba(25, 118, 210, 1), rgba(25, 118, 210, 1))'
      case 'project_billed': return 'linear-gradient(135deg, rgba(69, 160, 73, 1), rgba(69, 160, 73, 1))'
      case 'ege_energy_milestone': return 'linear-gradient(135deg, rgba(245, 124, 0, 1), rgba(245, 124, 0, 1))'
      default: return 'linear-gradient(135deg, rgba(51, 51, 51, 1), rgba(34, 34, 34, 1))'
    }
  }};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  z-index: 100;
  border-radius: 15px;
  border: 3px solid ${props => {
    switch (props.eventType) {
      case 'opportunity_accepted': return 'rgba(255, 193, 7, 1)'
      case 'new_opportunity': return '#2196F3'
      case 'project_billed': return '#4CAF50'
      case 'ege_energy_milestone': return '#F57C00'
      default: return '#333'
    }
  }};
  box-shadow: 0 0 30px ${props => {
    switch (props.eventType) {
      case 'opportunity_accepted': return 'rgba(76, 175, 80, 0.5)'
      case 'new_opportunity': return 'rgba(33, 150, 243, 0.5)'
      case 'project_billed': return 'rgba(255, 215, 0, 0.5)'
      case 'ege_energy_milestone': return 'rgba(255, 152, 0, 0.5)'
      default: return 'rgba(51, 51, 51, 0.5)'
    }
  }};
  backdrop-filter: blur(5px);
`

const NotificationIcon = styled.div`
  font-size: 80px;
  margin-bottom: 20px;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
`

const NotificationTitle = styled.h2`
  font-size: 80px;
  font-weight: 700;
  margin-bottom: 15px;
  color: white;
  text-align: center;
  text-shadow: 0 0 15px rgba(255, 255, 255, 0.3);
  text-transform: uppercase;
`

const NotificationMessage = styled.p`
  font-size: 60px;
  line-height: 1.4;
  margin-bottom: 20px;
  opacity: 0.95;
  text-align: center;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.2);
  text-transform: uppercase;
`

// Modificado: permite esconder o bloco de detalhes
const NotificationDetails = styled.div`
  font-size: 60px;
  opacity: 0.9;
  padding: 15px 25px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 15px;
  text-align: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  ${props => props.hide && `
    display: none;
  `}
  text-transform: uppercase;
`

const ConfettiContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 200;
`

const PinIndicator = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00d4ff;
  font-size: 18px;
  animation: ${props => props.isPinned ? 'pulse 2s infinite' : 'none'};
  
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
  }
`

// Adicionar componente para chuva de dinheiro
const MoneyRainContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 200;
  overflow: hidden;
`

const MoneyDrop = styled.div`
  position: absolute;
  top: -60px;
  left: ${props => props.left}%;
  font-size: ${props => props.size}px;
  opacity: 0.85;
  animation: money-fall-${props => props.id} ${props => props.duration}s linear forwards;
  filter: drop-shadow(0 0 8px #bfa100);

  @keyframes money-fall-${props => props.id} {
    0% {
      transform: translateX(0px) translateY(0) scale(1) rotate(${props => props.rotateStart}deg);
      opacity: 0.85;
    }
    30% {
      transform: translateX(${props => props.driftX}px) translateY(30vh) scale(1) rotate(${props => props.rotateStart + (props.rotateEnd - props.rotateStart) * 0.3}deg);
    }
    60% {
      transform: translateX(${props => -props.driftX}px) translateY(60vh) scale(1) rotate(${props => props.rotateStart + (props.rotateEnd - props.rotateStart) * 0.6}deg);
    }
    100% {
      transform: translateX(0px) translateY(100vh) scale(${props => props.scaleEnd}) rotate(${props => props.rotateEnd}deg);
      opacity: 0;
    }
  }
`


// Modificado: EGE energy não tem detalhes
const getNotificationConfig = (eventType, data) => {
  switch (eventType) {
    case 'opportunity_accepted':
      return {
        title: '🎉 Oportunidade Aceita!',
        message: `Nova oportunidade aceita!`,
        details: `${data.client}`,
        showDetails: true,
      }
    
    case 'new_opportunity':
      return {
        title: '📋 Novo Pedido!',
        message: `Novo pedido recebido!`,
        details: `${data.client} - ${data.description || 'Descrição não disponível'}`,
        showDetails: true,
      }
    
    case 'project_billed':
      return {
        title: '💰 Projeto Faturado!',
        message: `Projeto faturado com sucesso!`,
        details: `${data.client}`,
        showDetails: true,
      }
    
    case 'ege_energy_milestone':
      return {
        title: '⚡ EGE Energy! ⚡',
        message: `Já enviou o seu?`,

        showDetails: false,
      }
    
    default:
      return {
        icon: <FaCheckCircle />,
        title: 'Notificação',
        message: 'Nova notificação recebida',
        details: 'Detalhes não disponíveis',
        showDetails: true,
      }
  }
}

const ContentCarousel = ({ media = [], notification = null, isPinned = false }) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const [progress, setProgress] = useState(0)
  const audioRef = useRef(null)

  // Duração baseada na mídia atual ou padrão de 10 segundos
  const getCurrentSlideDuration = () => {
    if (media.length === 0) return 10000
    const currentMedia = media[currentSlide]
    // Converter duração de segundos para milissegundos, com mínimo de 5 segundos
    const duration = (currentMedia.duration || 10) * 1000
    return Math.max(duration, 5000) // Mínimo 5 segundos
  }

  useEffect(() => {
    if (!isPlaying || media.length === 0 || notification) return

    const slideDuration = getCurrentSlideDuration()
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % media.length)
      setProgress(0)
    }, slideDuration)

    return () => clearInterval(interval)
  }, [isPlaying, media.length, notification, currentSlide])

  useEffect(() => {
    if (!isPlaying || notification) return

    const slideDuration = getCurrentSlideDuration()
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + (100 / (slideDuration / 100))
        return newProgress >= 100 ? 0 : newProgress
      })
    }, 100)

    return () => clearInterval(progressInterval)
  }, [isPlaying, notification, currentSlide])

  // Tocar som ao exibir notificação
  useEffect(() => {
    if (!notification) return
    
    let audioSrc = null
    
    if (notification.eventType === 'opportunity_accepted') {
      // Usar o campo 'ordem' do WebSocket para escolher o som específico
      const ordem = notification.data?.ordem || 1
      switch (ordem) {
        case 1:
          audioSrc = accepted1
          break
        case 2:
          audioSrc = accepted2
          break
        case 3:
          audioSrc = accepted3
          break
        default:
          audioSrc = accepted1 // Fallback para accepted1
      }
    } else if (notification.eventType === 'new_opportunity') {
      audioSrc = newOpportunitySound
    } else if (notification.eventType === 'project_billed') {
      audioSrc = projectBilledSound
    } else if (notification.eventType === 'ege_energy_milestone') {
      audioSrc = egeEnergySound
    }
    
    if (audioSrc) {
      const audio = new window.Audio(audioSrc)
      audio.volume = 1.0
      
      // Tocar o áudio quando carregar
      audio.addEventListener('canplaythrough', () => {
        audio.play()
        audioRef.current = audio
      })
      
      audio.addEventListener('error', () => {
        console.warn('Erro ao carregar áudio:', audioSrc)
      })
    }
    
    // Parar o áudio se a notificação sumir
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [notification])

  // Notas de dinheiro para efeito de faturamento
  const bills = useMemo(() => {
    if (!notification || notification.eventType !== 'project_billed') return []
    return Array.from({ length: 38 }).map((_, i) => {
      const angle = Math.random() * 2 * Math.PI
      const radius = 200 + Math.random() * 400
      const tx = Math.cos(angle) * radius
      const ty = Math.sin(angle) * radius
      const r = Math.random() * 360 + 'deg'
      const s = 0.7 + Math.random() * 1.2
      const delay = (i * 0.04).toFixed(2) + 's'
      return { tx, ty, r, s, delay, i }
    })
  }, [notification])

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % media.length)
    setProgress(0)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + media.length) % media.length)
    setProgress(0)
  }

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying)
  }

  const [moneyRainNotes, setMoneyRainNotes] = useState([])
  const moneyRainInterval = useRef(null)

  useEffect(() => {
    if (!notification || notification.eventType !== 'project_billed') {
      setMoneyRainNotes([])
      if (moneyRainInterval.current) {
        clearInterval(moneyRainInterval.current)
        moneyRainInterval.current = null
      }
      return
    }

    // Função para adicionar uma nova nota
    const addNote = () => {
      const rotateStart = Math.random() * 360;
      const rotateEnd = rotateStart + (60 + Math.random() * 60) * (Math.random() > 0.5 ? 1 : -1);
      const driftX = Math.random() * 40 * (Math.random() > 0.5 ? 1 : -1);
      setMoneyRainNotes(prev => [
        ...prev,
        {
          id: Math.random().toString(36).substr(2, 9) + Date.now(),
          left: Math.random() * 100,
          size: 44 + Math.random() * 32,
          duration: 3.2 + Math.random() * 1.5,
          rotateStart,
          rotateEnd,
          driftX,
          scaleEnd: 0.7 + Math.random() * 0.6
        }
      ])
    }

    // Adiciona várias notas no início para dar volume
    for (let i = 0; i < 18; i++) addNote()

    // Adiciona novas notas continuamente
    moneyRainInterval.current = setInterval(() => {
      for (let i = 0; i < 2; i++) addNote()
    }, 120)

    return () => {
      if (moneyRainInterval.current) {
        clearInterval(moneyRainInterval.current)
        moneyRainInterval.current = null
      }
      setMoneyRainNotes([])
    }
  }, [notification && notification.id, notification && notification.eventType])

  // Remover notas que terminaram a animação
  const handleMoneyDropEnd = (id) => {
    setMoneyRainNotes(prev => prev.filter(note => note.id !== id))
  }

  if (media.length === 0) {
    return (
      <CarouselContainer>
        <MediaContainer>
          <MediaWrapper>
            <SlideContainer>
              <SlideImage src={fallbackImg} alt="Imagem padrão" />
              <SlideContent>
              </SlideContent>
            </SlideContainer>
          </MediaWrapper>
        </MediaContainer>
      </CarouselContainer>
    )
  }

  const currentMedia = media[currentSlide]
  
  // Debug da duração
  const currentDuration = getCurrentSlideDuration()
  console.log(`Mídia atual: ${currentMedia?.title}, Duração: ${currentDuration}ms (${currentDuration/1000}s)`)

  return (
    <CarouselContainer>
      <MediaContainer>
        <MediaWrapper>
          <AnimatePresence mode="wait">
            <SlideContainer
              key={currentSlide}
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.5 }}
            >
              <SlideImage src={currentMedia.file_url} alt={currentMedia.title} />
              
              <SlideContent>
              </SlideContent>

              {/* Notificação na moldura da mídia */}
              {notification && (
                <>
                  {notification.eventType === 'opportunity_accepted' && (
                    <NotificationOverlay
                      eventType={notification.eventType}
                      style={{ background: '#FFC107', color: '#fff', border: 'none', boxShadow: 'none' }}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.4, type: 'spring', stiffness: 200 }}
                    >
                      {/* Efeito de confete */}
                      <ConfettiContainer>
                        <Confetti
                          mode="fall"
                          particleCount={120}
                          colors={["#00d4ff", "#FFD700", "#4CAF50", "#fff", "#00d4ff"]}
                          spread={120}
                          duration={2200}
                          size={2.2}
                        />
                      </ConfettiContainer>
                      {/* Nome do cliente */}
                      <div style={{
                        fontSize: 64,
                        fontWeight: 400,
                        textAlign: 'center',
                        marginBottom: 40,
                        marginTop: 20,
                        color: '#fff',
                        textShadow: '0 0 10px #bfa100',
                        letterSpacing: 1,
                        textTransform: 'uppercase'
                      }}>
                        {notification.data?.client || 'NOME DO CLIENTE'}
                      </div>
                      {/* Mensagem e ID com sinos */}
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        textTransform: 'uppercase'
                      }}>
                        <div style={{ fontSize: 28, fontWeight: 300, marginBottom: 10, color: '#fff' }}>
                          OPORTUNIDADE ACEITA
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 60, width: '100%', textTransform: 'uppercase' }}>
                          {(() => {
                            const variation = notification.data?.variation || notification.data?.ordem || 1
                            let IconComponent = FaBell // Padrão: sino
                            if (variation === 2) IconComponent = GiGong // Gongo
                            else if (variation === 3) IconComponent = GiTribalMask // Haka
                            return (
                              <>
                                <IconComponent style={{ fontSize: 90, color: '#fff', filter: 'drop-shadow(0 0 8px #bfa100)', textTransform: 'uppercase' }} />
                                <span style={{ fontSize: 72, fontWeight: 700, color: '#fff', letterSpacing: 2 }}>
                                  {notification.data?.id_opt || notification.data?.bubble_id || notification.data?.id || 'ID NÃO DISPONÍVEL'}
                                </span>
                                <IconComponent style={{ fontSize: 90, color: '#fff', filter: 'drop-shadow(0 0 8px #bfa100)', textTransform: 'uppercase' }} />
                              </>
                            )
                          })()}
                        </div>
                      </div>
                      {/* Título do projeto */}
                      <div style={{
                        fontSize: 48,
                        fontWeight: 400,
                        textAlign: 'center',
                        marginTop: 40,
                        color: '#fff',
                        textShadow: '0 0 10px #bfa100',
                        letterSpacing: 1,
                        textTransform: 'uppercase'
                      }}>
                        {notification.data?.description || 'TÍTULO COMPLETO DO PROJETO'}
                      </div>
                      {/* Pin visual se fixado */}
                      {isPinned && (
                        <PinIndicator isPinned={isPinned}>
                          <FaThumbtack />
                        </PinIndicator>
                      )}
                    </NotificationOverlay>
                  )}
                  {notification && notification.eventType === 'new_opportunity' && (
                    <NotificationOverlay
                      eventType={notification.eventType}
                      style={{
                        background: 'rgb(4, 118, 217)',
                        color: '#fff',
                        border: 'none',
                        boxShadow: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100%',
                        minWidth: '100%',
                        textTransform: 'uppercase'
                      }}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.4, type: 'spring', stiffness: 200 }}
                    >
                      <div style={{ fontSize: 64, fontWeight: 400, textAlign: 'center', marginBottom: 40, marginTop: 20, color: '#fff', letterSpacing: 1, textTransform: 'uppercase' }}>
                        {notification.data?.client || 'NOME DO CLIENTE'}
                      </div>
                      <div style={{ fontSize: 28, fontWeight: 300, marginBottom: 10, color: '#fff', textAlign: 'center', letterSpacing: 1, textTransform: 'uppercase' }}>
                        OPORTUNIDADE REGISTRADA
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 60, width: '100%', textTransform: 'uppercase' }}>
                        <MdNewReleases style={{ fontSize: 90, color: '#FFD700', filter: 'drop-shadow(0 0 8px #bfa100)' }} />
                        <span style={{ fontSize: 72, fontWeight: 700, color: '#fff', letterSpacing: 2, textTransform: 'uppercase' }}>
                          {notification.data?.id_opt || notification.data?.bubble_id || notification.data?.id || 'ID NÃO DISPONÍVEL'}
                        </span>
                        <MdNewReleases style={{ fontSize: 90, color: '#FFD700', filter: 'drop-shadow(0 0 8px #bfa100)' }} />
                      </div>
                      <div style={{ fontSize: 48, fontWeight: 400, textAlign: 'center', marginTop: 40, color: '#fff', letterSpacing: 1, textTransform: 'uppercase' }}>
                        {notification.data?.description || 'TÍTULO COMPLETO DO PROJETO'}
                      </div>
                    </NotificationOverlay>
                  )}
                  {notification && notification.eventType === 'project_billed' && (
                    <NotificationOverlay
                      eventType={notification.eventType}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.4, type: 'spring', stiffness: 200 }}
                    >
                      {/* Chuva de dinheiro contínua */}
                      <MoneyRainContainer>
                        {moneyRainNotes.map(drop => (
                          <MoneyDrop
                            key={drop.id}
                            id={drop.id}
                            left={drop.left}
                            size={drop.size}
                            duration={drop.duration}
                            rotateStart={drop.rotateStart}
                            rotateEnd={drop.rotateEnd}
                            driftX={drop.driftX}
                            scaleEnd={drop.scaleEnd}
                            onAnimationEnd={() => handleMoneyDropEnd(drop.id)}
                          >
                          
                          💰
                          </MoneyDrop>
                        ))}
                      </MoneyRainContainer>
                      {/* Bloco visual da notificação */}
                      <div style={{
                        fontSize: 64,
                        fontWeight: 400,
                        textAlign: 'center',
                        marginBottom: 40,
                        marginTop: 20,
                        color: '#fff',
                        textShadow: '0 0 10px #bfa100',
                        letterSpacing: 1,
                        textTransform: 'uppercase'
                      }}>
                        {notification.data?.client || 'NOME DO CLIENTE'}
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        textTransform: 'uppercase'
                      }}>
                        <div style={{ fontSize: 28, fontWeight: 300, marginBottom: 10, color: '#fff', textTransform: 'uppercase' }}>
                          FATURAMENTO REALIZADO
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 60, width: '100%', textTransform: 'uppercase' }}>
                          <FaMoneyBillWave style={{ fontSize: 90, color: '#fff', filter: 'drop-shadow(0 0 8px #bfa100)' }} />
                          <span style={{ fontSize: 72, fontWeight: 700, color: '#fff', letterSpacing: 2, textTransform: 'uppercase' }}>
                            {notification.data?.id_opt || notification.data?.bubble_id || notification.data?.id || 'ID NÃO DISPONÍVEL'}
                          </span>
                          <FaMoneyBillWave style={{ fontSize: 90, color: '#fff', filter: 'drop-shadow(0 0 8px #bfa100)' }} />
                        </div>
                      </div>
                      <div style={{
                        fontSize: 48,
                        fontWeight: 400,
                        textAlign: 'center',
                        marginTop: 40,
                        color: '#fff',
                        textShadow: '0 0 10px #bfa100',
                        letterSpacing: 1,
                        textTransform: 'uppercase'
                      }}>
                        {notification.data?.description || 'TÍTULO COMPLETO DO PROJETO'}
                      </div>
                      {isPinned && (
                        <PinIndicator isPinned={isPinned}>
                          <FaThumbtack />
                        </PinIndicator>
                      )}
                    </NotificationOverlay>
                  )}
                  {notification && notification.eventType === 'ege_energy_milestone' && (
                    <NotificationOverlay
                      eventType={notification.eventType}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ 
                        duration: 0.4,
                        type: "spring",
                        stiffness: 200
                      }}
                    >
                      {/* Indicador de fixação */}
                      {isPinned && (
                        <PinIndicator isPinned={isPinned}>
                          <FaThumbtack />
                        </PinIndicator>
                      )}
                      {/* Efeito de confete para oportunidade aceita */}
                      {notification.eventType === 'opportunity_accepted' && (
                        <ConfettiContainer>
                          <Confetti
                            mode="fall"
                            particleCount={120}
                            colors={["#00d4ff", "#FFD700", "#4CAF50", "#fff", "#00d4ff"]}
                            spread={120}
                            duration={2200}
                            size={2.2}
                          />
                        </ConfettiContainer>
                      )}
                      {/* Efeito de dinheiro explodindo para faturamento */}
                      {notification.eventType === 'project_billed' &&
                        bills.map(({ tx, ty, r, s, delay, i }) => (
                          <Bill
                            key={i}
                            style={{
                              left: '50%',
                              top: '50%',
                              transform: 'translate(-50%, -50%)',
                              '--tx': `${tx}px`,
                              '--ty': `${ty}px`,
                              '--r': r,
                              '--s': s,
                              '--delay': delay
                            }}
                          >
                            💸
                          </Bill>
                        ))
                      }
                      {(() => {
                        const config = getNotificationConfig(notification.eventType, notification.data)
                        return (
                          <>
                            <NotificationIcon>
                              {config.icon}
                            </NotificationIcon>
                            <NotificationTitle>{config.title}</NotificationTitle>
                            <NotificationMessage>{config.message}</NotificationMessage>
                            {/* Só mostra detalhes se showDetails for true e houver texto */}
                            {config.showDetails && config.details && (
                              <NotificationDetails>{config.details}</NotificationDetails>
                            )}
                          </>
                        )
                      })()}
                    </NotificationOverlay>
                  )}
                </>
              )}
            </SlideContainer>
          </AnimatePresence>
        </MediaWrapper>
      </MediaContainer>

      <ProgressBar>
        <ProgressFill
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.1 }}
        />
      </ProgressBar>
    </CarouselContainer>
  )
}

export default ContentCarousel